import {all, delay, takeLatest} from '@redux-saga/core/effects';
import {ImagesDto, ImageSearchResponseDto, PolicyDto} from 'Agent/Policies/api';
import policyApi from 'Agent/Policies/api/policyApi';
import {
  changePendingUnderwritingRequirements,
  changePolicies,
  changePolicy,
  changePolicyImages,
  fetchPendingUnderwritingRequirements,
  fetchPolicies,
  fetchPolicy,
  fetchPolicyImages,
  PolicyActionTypes,
} from 'Agent/Policies/ducks/policy';
import {AsyncResult, Loaded, Loading, LoadingAdditional} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {call, put} from 'redux-saga/effects';
import {DataSource} from 'Shared/utilities/DataSourceUtility';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';

export type PolicyImages = ImageSearchResponseDto;
export type PolicyImage = ImagesDto;

export function* getPoliciesFunction(action: ReturnType<typeof fetchPolicies>) {
  const {sort, pageOption, isNewYork, encryptedIdentifier, filter, agentNumbers} = action.payload;

  // [B-994651] - [31.05.2022] - Adding a 1500ms delay to prevent unnecessary api calls while the user is still typing the filter value
  if (filter) {
    const {accountNumber, policyNumber, payorName, insuredName, accountName} = filter;
    if (policyNumber !== '' || accountNumber !== '' || accountName !== '' || insuredName !== '' || payorName !== '')
      yield delay(1500);
  }

  if (pageOption) {
    pageOption.links.forEach(x => {
      if (!x.href.includes('api/policies')) {
        x.href = 'api/policies' + x.href;
      }
    });
    yield put(changePolicies(new LoadingAdditional(pageOption)));
  } else {
    yield put(changePolicies(new Loading(pageOption)));
  }

  const result: AsyncResult<Page<PolicyDto>> = yield pageOption
    ? call(mergeWithNextPage, {existingPage: pageOption})
    : call(policyApi.searchPolicies, agentNumbers.map(prop('value')), isNewYork, encryptedIdentifier, filter, sort);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changePolicies(value)),
      value => put(changePolicies(value))
    )
  );
}

export function* fetchPoliciesSaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICIES, getPoliciesFunction);
}

export function* fetchPolicyImagesApi(action: ReturnType<typeof fetchPolicyImages>) {
  const {encryptedIdentifier, policyNumber, policyId, source} = action.payload;
  const eisFlag = Number(source) === DataSource.EIS;
  const id = eisFlag ? policyNumber : policyId;
  yield getApiCallGenerator(() => policyApi.getImages(encryptedIdentifier, id, eisFlag), changePolicyImages)(action);
}

export function* fetchPolicyImagesSaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICY_IMAGES, fetchPolicyImagesApi);
}

const getPendingUnderwritingRequirementsFunction = (action: ReturnType<typeof fetchPendingUnderwritingRequirements>) =>
  policyApi.getPendingUnderwritingRequirements(
    action.payload.encryptedIdentifier,
    action.payload.agentNumbers,
    action.payload.isNewYork
  );

export function* fetchPendingUnderwritingRequirementsSaga() {
  yield takeLatest(
    PolicyActionTypes.FETCH_PENDING_UNDERWRITING_REQUIREMENTS,
    getApiCallGenerator(getPendingUnderwritingRequirementsFunction, changePendingUnderwritingRequirements)
  );
}

export const getPolicyFunction = (action: ReturnType<typeof fetchPolicy>) => {
  const {policyId, encryptedIdentifier, policyNumber, source, producerCodes} = action.payload;
  const eisFlag = Number(source) === DataSource.EIS;
  const id = eisFlag ? policyNumber : policyId;
  let bodyParams = new URLSearchParams();
  bodyParams.append('encryptedIdentifier', encryptedIdentifier);
  bodyParams.append('id', id);
  bodyParams.append('producerCodes', producerCodes.map(prop('value')).join(','));
  bodyParams.append('policyNumber', policyNumber);
  bodyParams.append('eisFlag', JSON.stringify(eisFlag));
  return policyApi.getPolicy(bodyParams);
};

export function* fetchPolicySaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICY, getApiCallGenerator(getPolicyFunction, changePolicy));
}

export default function*() {
  yield all([
    fetchPoliciesSaga(),
    fetchPolicySaga(),
    fetchPolicyImagesSaga(),
    fetchPendingUnderwritingRequirementsSaga(),
  ]);
}
